// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import ProductActions, {
    ProductActionsProps,
    ProductActionsStyle,
} from "@pacific/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@pacific/App/Components/Product/ProductGridTemplate";
import ProductPrice, { ProductPriceStyles } from "@pacific/Overrides/Components/ProductPrice";
import CaretRight from "@pacific/XDL/Icons/CaretRight";
import { getSystemColor } from "@pacific/XDL/Utilities";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    // XNG-Changes:
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
}

const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {
    productActionsStyles: {
        addToCartContainer: {
            css: css`
                justify-content: space-between;
            `,
        },
        addToCartButtonWrapper: {
            css: css`
                flex-grow: 1;
            `,
        },
    },
    productListProductImageStyles: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        compareCheckbox: {
            css: css`
                margin: 0;
            `,
        },
    },
    priceAvailabilityLink: {
        color: "text.link",
        hoverStyle: {
            color: getSystemColor("common.ui8"),
        },
        icon: {
            iconProps: {
                src: CaretRight,
                size: 5,
                color: "text.link",
            },
            position: "right",
        },
        typographyProps: {
            size: 10,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 13px;
            `,
        },
        css: css`
            ${IconWrapper} {
                margin-left: 4px;
            }
        `,
    },
};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));
    const history = useHistory();

    const priceAvailabilityLinkClickHandler = () => {
        if (!signInUrl) {
            history.push("/MyAccount/SignIn");
        } else {
            history.push(`${signInUrl}`);
        }
    };

    return (
        <ProductGridContainer extendedStyles={styles.productGridContainer}>
            <ProductGridContainer.PriceAndActions>
                {showPrice && (
                    <ProductGridContainer.Price>
                        <ProductPrice
                            product={productContext}
                            showInvalidPriceMessage={true}
                            extendedStyles={styles.productPriceStyles}
                        />
                    </ProductGridContainer.Price>
                )}
                {canSeePrices && showAvailability && (
                    <ProductGridContainer.Availability>
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            {...{ hideLink: hideAvailabilityLink }}
                        />
                    </ProductGridContainer.Availability>
                )}
                {canSeePrices && showAddToCartActions && (
                    <ProductGridContainer.AddToCartActions>
                        <ProductActions
                            {...productActionsProps}
                            onAddToCartClick={onAddToCartClick}
                            showDisabled={showDisabled}
                            iconOnly={showAddToCartIcon}
                            extendedStyles={styles.productActionsStyles}
                        />
                    </ProductGridContainer.AddToCartActions>
                )}
                {canSeePrices && (showAddToList || showCompare) && (
                    <ProductGridContainer.AddToList>
                        {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            extendedStyles={styles.productListProductImageStyles}
                        />
                    </ProductGridContainer.AddToList>
                )}
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
